/**
 * Session Statistics & Analytics
 */

/**
 * Get all session history
 */
export function getSessionHistory() {
  try {
    const data = localStorage.getItem('langi_sessions');
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('Error loading sessions:', error);
    return [];
  }
}

/**
 * Save session to history
 */
export function saveSession(session) {
  try {
    const sessions = getSessionHistory();
    sessions.push({
      ...session,
      timestamp: Date.now(),
      date: new Date().toISOString().split('T')[0],
    });
    
    // Keep only last 90 days
    const ninetyDaysAgo = Date.now() - (90 * 24 * 60 * 60 * 1000);
    const filtered = sessions.filter(s => s.timestamp > ninetyDaysAgo);
    
    localStorage.setItem('langi_sessions', JSON.stringify(filtered));
  } catch (error) {
    console.error('Error saving session:', error);
  }
}

/**
 * Get statistics for a date range
 */
export function getStatistics(days = 30) {
  const sessions = getSessionHistory();
  const cutoff = Date.now() - (days * 24 * 60 * 60 * 1000);
  const recentSessions = sessions.filter(s => s.timestamp > cutoff);
  
  if (recentSessions.length === 0) {
    return {
      totalSessions: 0,
      totalQuestions: 0,
      totalCorrect: 0,
      totalWrong: 0,
      averageAccuracy: 0,
      averageScore: 0,
      bestScore: 0,
      bestStreak: 0,
      totalTime: 0,
      averageTime: 0,
      setsPlayed: [],
      dailyStats: [],
    };
  }
  
  const totalQuestions = recentSessions.reduce((sum, s) => sum + s.totalAnswered, 0);
  const totalCorrect = recentSessions.reduce((sum, s) => sum + s.correctAnswers, 0);
  const totalWrong = recentSessions.reduce((sum, s) => sum + (s.totalAnswered - s.correctAnswers), 0);
  const totalTime = recentSessions.reduce((sum, s) => sum + (s.duration || 0), 0);
  
  const bestScore = Math.max(...recentSessions.map(s => s.score || 0));
  const bestStreak = Math.max(...recentSessions.map(s => s.maxStreak || 0));
  
  // Group by date
  const byDate = {};
  recentSessions.forEach(session => {
    const date = session.date;
    if (!byDate[date]) {
      byDate[date] = {
        date,
        sessions: 0,
        questions: 0,
        correct: 0,
        wrong: 0,
        score: 0,
      };
    }
    byDate[date].sessions++;
    byDate[date].questions += session.totalAnswered;
    byDate[date].correct += session.correctAnswers;
    byDate[date].wrong += (session.totalAnswered - session.correctAnswers);
    byDate[date].score += session.score || 0;
  });
  
  const dailyStats = Object.values(byDate).sort((a, b) => 
    new Date(a.date) - new Date(b.date)
  );
  
  // Get unique sets played
  const setsPlayed = [...new Set(recentSessions.map(s => s.setName))];
  
  return {
    totalSessions: recentSessions.length,
    totalQuestions,
    totalCorrect,
    totalWrong,
    averageAccuracy: totalQuestions > 0 ? ((totalCorrect / totalQuestions) * 100).toFixed(1) : 0,
    averageScore: (recentSessions.reduce((sum, s) => sum + (s.score || 0), 0) / recentSessions.length).toFixed(1),
    bestScore,
    bestStreak,
    totalTime,
    averageTime: recentSessions.length > 0 ? Math.round(totalTime / recentSessions.length) : 0,
    setsPlayed,
    dailyStats,
  };
}

/**
 * Get current streak (consecutive days)
 */
export function getCurrentStreak() {
  const sessions = getSessionHistory();
  if (sessions.length === 0) return 0;
  
  // Group by date
  const dates = [...new Set(sessions.map(s => s.date))].sort().reverse();
  
  const today = new Date().toISOString().split('T')[0];
  let streak = 0;
  let checkDate = new Date(today);
  
  for (const date of dates) {
    const dateStr = checkDate.toISOString().split('T')[0];
    if (date === dateStr) {
      streak++;
      checkDate.setDate(checkDate.getDate() - 1);
    } else {
      break;
    }
  }
  
  return streak;
}

/**
 * Get best streak ever
 */
export function getBestStreak() {
  const sessions = getSessionHistory();
  if (sessions.length === 0) return 0;
  
  const dates = [...new Set(sessions.map(s => s.date))].sort();
  
  let currentStreak = 1;
  let bestStreak = 1;
  
  for (let i = 1; i < dates.length; i++) {
    const prevDate = new Date(dates[i - 1]);
    const currDate = new Date(dates[i]);
    const diffDays = Math.round((currDate - prevDate) / (24 * 60 * 60 * 1000));
    
    if (diffDays === 1) {
      currentStreak++;
      bestStreak = Math.max(bestStreak, currentStreak);
    } else {
      currentStreak = 1;
    }
  }
  
  return bestStreak;
}

/**
 * Get set-specific statistics
 */
export function getSetStatistics(setName) {
  const sessions = getSessionHistory();
  const setSessions = sessions.filter(s => s.setName === setName);
  
  if (setSessions.length === 0) {
    return {
      timesPlayed: 0,
      totalQuestions: 0,
      averageAccuracy: 0,
      bestScore: 0,
      lastPlayed: null,
    };
  }
  
  const totalQuestions = setSessions.reduce((sum, s) => sum + s.totalAnswered, 0);
  const totalCorrect = setSessions.reduce((sum, s) => sum + s.correctAnswers, 0);
  const bestScore = Math.max(...setSessions.map(s => s.score || 0));
  const lastPlayed = Math.max(...setSessions.map(s => s.timestamp));
  
  return {
    timesPlayed: setSessions.length,
    totalQuestions,
    averageAccuracy: totalQuestions > 0 ? ((totalCorrect / totalQuestions) * 100).toFixed(1) : 0,
    bestScore,
    lastPlayed: new Date(lastPlayed).toLocaleDateString(),
  };
}

/**
 * Get accuracy trend (last 7 days)
 */
export function getAccuracyTrend() {
  const stats = getStatistics(7);
  return stats.dailyStats.map(day => ({
    date: day.date,
    accuracy: day.questions > 0 ? ((day.correct / day.questions) * 100).toFixed(1) : 0,
  }));
}

/**
 * Export statistics as JSON
 */
export function exportStatistics() {
  const sessions = getSessionHistory();
  const stats = getStatistics(90);
  
  const data = {
    exportDate: new Date().toISOString(),
    summary: stats,
    sessions,
  };
  
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `langi_statistics_${Date.now()}.json`;
  a.click();
  URL.revokeObjectURL(url);
}
